import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Cake, Gift, Loader2, UserPlus } from "lucide-react";
import { homeService } from "../../services/api";
import { getCurrentUserId } from "../../utils/navbarNotifications";
import { useIsPhone } from "../../hooks/useIsPhone";

/**
 * Shared Birthdays / Work Anniversaries / New Joinees cards fed from DB.
 */
export default function HomeHighlightsPanel({
  darkMode = false,
  showBirthdays = true,
  showAnniversaries = true,
  showNewJoinees = true,
  onViewEmployee = null,
}) {
  const [loading, setLoading] = useState(true);
  const [birthdays, setBirthdays] = useState([]);
  const [anniversaries, setAnniversaries] = useState([]);
  const [newJoinees, setNewJoinees] = useState([]);
  const [busyKey, setBusyKey] = useState(null);
  const isPhone = useIsPhone();
  const me = getCurrentUserId();

  const border = darkMode ? "border-[#3C3C4C]" : "border-gray-200";
  const cardBg = darkMode ? "bg-[#1D1D1D]" : "bg-white";
  const itemBg = darkMode ? "border-[#3C3C4C] bg-[#232323]" : "border-gray-100 bg-gray-50";
  const titleCls = darkMode ? "text-gray-100" : "text-gray-900";
  const mutedCls = darkMode ? "text-gray-400" : "text-gray-600";
  const softCls = darkMode ? "text-gray-500" : "text-gray-500";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await homeService.getHighlights({ days: 7 });
      const data = res?.data || {};
      setBirthdays(Array.isArray(data.birthdays) ? data.birthdays : []);
      setAnniversaries(Array.isArray(data.anniversaries) ? data.anniversaries : []);
      setNewJoinees(Array.isArray(data.newJoinees) ? data.newJoinees : []);
    } catch (err) {
      setBirthdays([]);
      setAnniversaries([]);
      setNewJoinees([]);
      toast.error(err?.response?.data?.message || err?.message || "Could not load highlights");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const send = async (type, person) => {
    if (!person?.employeeId) return;
    if (me != null && Number(person.userId) === Number(me)) {
      toast.error("You cannot send this to yourself");
      return;
    }
    const key = `${type}:${person.employeeId}`;
    setBusyKey(key);
    try {
      const res = await homeService.sendCelebration({
        type,
        employeeId: person.employeeId,
      });
      if (res?.alreadySent) toast("Already sent today", { icon: "✓" });
      else toast.success(res?.message || "Sent");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to send");
    } finally {
      setBusyKey(null);
    }
  };

  const empty = (label) => (
    <p className={`rounded-xl border border-dashed px-3 py-6 text-center text-xs ${border} ${mutedCls}`}>
      No {label} in the next 7 days.
    </p>
  );

  if (loading) {
    return (
      <div className={`flex items-center justify-center gap-2 rounded-2xl border p-8 ${border} ${cardBg}`}>
        <Loader2 className="h-4 w-4 animate-spin text-redhalo-red" />
        <span className={`text-sm ${mutedCls}`}>Loading highlights…</span>
      </div>
    );
  }

  const shown = [
    showBirthdays && { label: "birthdays", count: birthdays.length, Icon: Cake },
    showAnniversaries && { label: "anniversaries", count: anniversaries.length, Icon: Gift },
    showNewJoinees && { label: "new joinees", count: newJoinees.length, Icon: UserPlus },
  ].filter(Boolean);

  if (isPhone && shown.length && shown.every((s) => !s.count)) {
    const labels = shown.map((s) => s.label);
    const list =
      labels.length > 1
        ? `${labels.slice(0, -1).join(", ")} or ${labels[labels.length - 1]}`
        : labels[0];
    return (
      <div className={`flex items-center gap-3 rounded-2xl border px-4 py-3 ${border} ${cardBg}`}>
        <div className="flex shrink-0 -space-x-1.5">
          {shown.map(({ label, Icon }) => (
            <span
              key={label}
              className={`flex h-7 w-7 items-center justify-center rounded-full bg-redhalo-red/10 ring-2 ${
                darkMode ? "ring-[#1D1D1D]" : "ring-white"
              }`}
            >
              <Icon className="h-3.5 w-3.5 text-redhalo-red" aria-hidden="true" />
            </span>
          ))}
        </div>
        <p className={`text-xs ${mutedCls}`}>No {list} in the next 7 days.</p>
      </div>
    );
  }

  const wishBtn = (person, type, label, emerald = false) => {
    const key = `${type}:${person.employeeId}`;
    const isMe = me != null && Number(person.userId) === Number(me);
    return (
      <button
        type="button"
        disabled={!!busyKey || isMe}
        onClick={() => send(type, person)}
        title={isMe ? "That’s you" : label}
        className={`rounded-md px-2 py-1 text-[11px] font-semibold transition disabled:opacity-40 ${
          emerald
            ? darkMode
              ? "bg-emerald-900/40 text-emerald-200 hover:bg-emerald-900/60"
              : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
            : darkMode
              ? "bg-redhalo-red/20 text-redhalo-mint hover:bg-redhalo-red/30"
              : "bg-redhalo-red/10 text-redhalo-red hover:bg-redhalo-red/20"
        }`}
      >
        {busyKey === key ? "…" : label}
      </button>
    );
  };

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
      {showBirthdays && (
        <div className={`rounded-2xl border p-5 ${border} ${cardBg}`}>
          <div className="mb-3 flex items-center gap-2">
            <Cake className="h-5 w-5 text-redhalo-red" />
            <h2 className={`text-base font-semibold ${titleCls}`}>Birthdays</h2>
          </div>
          <div className="space-y-3">
            {!birthdays.length
              ? empty("birthdays")
              : birthdays.map((person) => (
                  <div key={`bday-${person.employeeId}`} className={`rounded-xl border p-3 ${itemBg}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-2">
                        <div
                          className={`flex h-8 w-8 items-center justify-center rounded-full text-[11px] font-semibold ${
                            darkMode ? "bg-[#2f2f2f] text-redhalo-mint" : "bg-redhalo-gray/50 text-redhalo-red"
                          }`}
                        >
                          {person.initials}
                        </div>
                        <div>
                          <p className={`text-sm font-semibold ${titleCls}`}>{person.name}</p>
                          <p className={`text-xs ${mutedCls}`}>
                            {person.team} - {person.when}
                          </p>
                          <p className={`mt-0.5 text-[11px] ${softCls}`}>{person.note || person.role}</p>
                        </div>
                      </div>
                      {wishBtn(person, "birthday_wish", "Wish")}
                    </div>
                  </div>
                ))}
          </div>
        </div>
      )}

      {showAnniversaries && (
        <div className={`rounded-2xl border p-5 ${border} ${cardBg}`}>
          <div className="mb-3 flex items-center gap-2">
            <Gift className="h-5 w-5 text-redhalo-red" />
            <h2 className={`text-base font-semibold ${titleCls}`}>Work Anniversaries</h2>
          </div>
          <div className="space-y-3">
            {!anniversaries.length
              ? empty("anniversaries")
              : anniversaries.map((person) => (
                  <div key={`ann-${person.employeeId}`} className={`rounded-xl border p-3 ${itemBg}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className={`text-sm font-semibold ${titleCls}`}>{person.name}</p>
                        <p className={`text-xs ${mutedCls}`}>
                          {person.years} year{person.years === 1 ? "" : "s"} - {person.when}
                        </p>
                        <p className={`mt-0.5 text-[11px] ${softCls}`}>{person.team}</p>
                      </div>
                      {wishBtn(person, "anniversary_celebrate", "Celebrate")}
                    </div>
                  </div>
                ))}
          </div>
        </div>
      )}

      {showNewJoinees && (
        <div className={`rounded-2xl border p-5 ${border} ${cardBg}`}>
          <div className="mb-3 flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-redhalo-red" />
            <h2 className={`text-base font-semibold ${titleCls}`}>New Joinees</h2>
          </div>
          <div className="space-y-3">
            {!newJoinees.length
              ? empty("new joinees")
              : newJoinees.map((person) => (
                  <div key={`join-${person.employeeId}`} className={`rounded-xl border p-3 ${itemBg}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className={`text-sm font-semibold ${titleCls}`}>{person.name}</p>
                        <p className={`text-xs ${mutedCls}`}>
                          {person.role} - {person.when}
                        </p>
                        <p className={`mt-0.5 text-[11px] ${softCls}`}>Buddy: {person.buddy || "—"}</p>
                      </div>
                      <div className="flex flex-col gap-1">
                        {wishBtn(person, "welcome", "Send Welcome", true)}
                        {typeof onViewEmployee === "function" && (
                          <button
                            type="button"
                            onClick={() => onViewEmployee(person)}
                            className={`rounded-md px-2 py-1 text-[11px] font-semibold transition ${
                              darkMode
                                ? "bg-[#2a2a2a] text-gray-200 hover:text-redhalo-mint"
                                : "bg-white text-gray-700 hover:text-redhalo-red"
                            }`}
                          >
                            View
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
          </div>
        </div>
      )}
    </div>
  );
}
